'use client';

import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';

import Button from '../../basic/Button';
import Input from '../../basic/Input';
import { atId, cn, getBirthdayDate } from '../../utils';
import dayjs from '../../utils/dayjs';

import AgeGateContext from './context';
import constants from './constants';

type AgeGateFormProps = {
  onAgeGatePassed?: (userYear: number) => void;
  onAgeGateBlocked?: () => void;
  labels?: {
    errorOnRequired?: string;
    errorOnInvalid?: string;
    dayInputTitle?: string;
    monthInputTitle?: string;
    yearInputTitle?: string;
    submitButtonText?: string;
  };
  errorContainerProps?: React.ComponentProps<'span'>;
  submitButtonProps?: React.ComponentProps<typeof Button>;
  inputClasses?: {
    label?: string;
    input?: string;
    root?: string;
    container?: string;
  };
  inputWrapperProps?: React.ComponentProps<'div'>;
  ageLimit?: number;
};

type State = { day: string; month: string; year: string };
type Action = { type: keyof State; value: string };

export default function AgeGateForm(props: AgeGateFormProps) {
  const [error, setError] = useState('');
  const { setBirthdate } = useContext(AgeGateContext);

  const [state, dispatch] = useReducer(
    (prevState: State, action: Action) => {
      switch (action.type) {
        case 'day':
          return { ...prevState, day: action.value };
        case 'month':
          return { ...prevState, month: action.value };
        case 'year':
          return { ...prevState, year: action.value };
        default:
          throw new Error();
      }
    },
    { day: '', month: '', year: '' },
  );

  useEffect(() => {
    setError('');
  }, [state]);

  const refDay = useRef<HTMLInputElement>(null);
  const refMonth = useRef<HTMLInputElement>(null);
  const refYear = useRef<HTMLInputElement>(null);
  const refSubmit = useRef<HTMLButtonElement>(null);

  const set = [
    [refDay, 2, 'day', state.day],
    [refMonth, 2, 'month', state.month],
    [refYear, 4, 'year', state.year],
  ] as const;

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = event.target;
    const [ref, maxLength, type] = set[index];

    if (value.length <= maxLength) dispatch({ type, value });

    if (value.length >= maxLength) {
      const next = set[(index + 1) % 3];
      const [nextRef, nextLength, , nextV] = next;

      const focusTarget =
        nextV.length < nextLength
          ? nextRef
          : (() => {
              const [fallbackRef] = set[set.findIndex(([, l, , v]) => v.length < l)] || [];
              if (fallbackRef && fallbackRef !== ref) return fallbackRef;
              return refSubmit;
            })();

      focusTarget.current?.focus();
    }
  }

  return (
    <form
      data-error={!!error}
      className={cn({
        'animate-[pulse_.5s_ease-in-out_3]': !!error,
      })}
      noValidate
      // eslint-disable-next-line consistent-return
      onSubmit={(e) => {
        e.preventDefault();

        const unfilled = set[set.findIndex(([, , , v]) => !v)];
        if (unfilled) return setError(props.labels?.errorOnRequired || 'This field is required.');

        const date = dayjs(getBirthdayDate(state), 'YYYY-MM-DD', true);
        const currentDate = dayjs();
        const currentYear = currentDate.year();
        const userYear = date.year();
        const userMonth = date.month();
        const userDay = date.date();
        if (!date.isValid() || userYear < 1900 || userYear > currentYear) {
          return setError(props.labels?.errorOnInvalid || 'Invalid date of birth.');
        }

        const age = currentYear - userYear;

        const ageLimit = props.ageLimit || constants.DEFAULT_AGE_LIMIT;
        const isUnderAge = age < ageLimit;
        const isExactAgeButNotYet =
          age === ageLimit &&
          (userMonth > currentDate.month() ||
            (userMonth === currentDate.month() && userDay > currentDate.date()));

        if (isUnderAge || isExactAgeButNotYet) {
          props.onAgeGateBlocked?.();
        } else {
          props.onAgeGatePassed?.(userYear);
        }

        setBirthdate(date.format('DD-MM-YYYY'));
      }}
    >
      <div className="mt-6 flex gap-2 text-start">
        <div className="flex-[27]">
          <Input
            error={!!error}
            label={props.labels?.dayInputTitle || 'Day'}
            labelProps={{
              className: props.inputClasses?.label,
            }}
            className={props.inputClasses?.root}
            containerProps={{
              className: props.inputClasses?.container,
            }}
            inputProps={{
              ref: refDay,
              placeholder: 'XX',
              className: props.inputClasses?.input,
              type: 'number',
              pattern: '[0-9]*',
              onChange: (e) => handleInputChange(e, 0),
              value: state.day,
            }}
            wrapperProps={{
              ['data-test-id' as 'datatype']: atId('ageGateDay', 'Inp'),
              ...(props.inputWrapperProps || {}),
            }}
          />
        </div>

        <div className="flex-[27]">
          <Input
            error={!!error}
            label={props.labels?.monthInputTitle || 'Month'}
            labelProps={{
              className: props.inputClasses?.label,
            }}
            className={props.inputClasses?.root}
            containerProps={{
              className: props.inputClasses?.container,
            }}
            inputProps={{
              ref: refMonth,
              placeholder: 'XX',
              className: props.inputClasses?.input,
              maxLength: 2,
              type: 'number',
              pattern: '[0-9]*',
              onChange: (e) => handleInputChange(e, 1),
              value: state.month,
            }}
            wrapperProps={{
              ['data-test-id' as 'datatype']: atId('ageGateMonth', 'Inp'),
              ...(props.inputWrapperProps || {}),
            }}
          />
        </div>

        <div className="flex-[46]">
          <Input
            error={!!error}
            label={props.labels?.yearInputTitle || 'Year'}
            labelProps={{
              className: props.inputClasses?.label,
            }}
            className={props.inputClasses?.root}
            containerProps={{
              className: props.inputClasses?.container,
            }}
            inputProps={{
              ref: refYear,
              placeholder: 'XXXX',
              className: props.inputClasses?.input,
              maxLength: 4,
              type: 'number',
              pattern: '[0-9]*',
              onChange: (e) => handleInputChange(e, 2),
              value: state.year,
            }}
            wrapperProps={{
              ['data-test-id' as 'datatype']: atId('ageGateYear', 'Inp'),
              ...(props.inputWrapperProps || {}),
            }}
          />
        </div>
      </div>

      <span
        data-test-id={atId('ageGateError', 'Val')}
        {...(props.errorContainerProps || {})}
        className={cn(
          'mt-3 block text-start font-body-s [&]:text-body-s text-error-30',
          props.errorContainerProps?.className,
        )}
      >
        {error}
      </span>

      <Button
        {...(props.submitButtonProps || {})}
        buttonProps={{
          type: 'submit',
          ['data-test-id' as 'datatype']: atId('ageGateEnter', 'Btn'),
          ...(props.submitButtonProps?.buttonProps || {}),
          ref: refSubmit,
          className: cn(
            'w-full mt-6 font-subtitle-m [&]:text-subtitle-m text-on-primary',
            props.submitButtonProps?.buttonProps?.className,
          ),
        }}
      >
        {props.labels?.submitButtonText || 'Enter'}
      </Button>
    </form>
  );
}
